import { useTranslation } from "react-i18next";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { POSTAL_CODE_STATUS } from "./usePostalCodeLookup";

/**
 * Visual feedback for the billing postal-code check (see usePostalCodeLookup).
 *
 * <PostalCodeStatusIcon> sits inside the input:  spinner while checking,
 *                                                check mark once confirmed.
 * <PostalCodeStatusMessage> sits under the input: "Checking postal code…",
 *                                                or a retry link if the check failed.
 * "Not found" needs neither: the input already shows its red error message.
 */

const GOLD = "#DBBE78";
const SUCCESS_GREEN = "#3CAEA3";

const Spinner = () => {
    const shouldReduce = useReducedMotion();
    return (
        <motion.span
            className="tw:block tw:h-4 tw:w-4 tw:rounded-full tw:border-2 tw:border-[#F1E4C3]"
            style={{ borderTopColor: GOLD }}
            animate={shouldReduce ? { opacity: [0.4, 1, 0.4] } : { rotate: 360 }}
            transition={{ duration: shouldReduce ? 1.2 : 0.8, ease: "linear", repeat: Infinity }}
            aria-hidden="true"
        />
    );
};

// Circle + tick that draw themselves in.
const DrawnCheck = () => (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <motion.circle
            cx="10" cy="10" r="9" stroke={SUCCESS_GREEN} strokeWidth="1.5"
            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.35 }}
        />
        <motion.path
            d="M6 10.5l2.6 2.6L14 7.5" stroke={SUCCESS_GREEN} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.3, delay: 0.25 }}
        />
    </svg>
);

const iconSwap = {
    initial: { opacity: 0, scale: 0.6 },
    animate: { opacity: 1, scale: 1, transition: { duration: 0.2 } },
    exit: { opacity: 0, scale: 0.6, transition: { duration: 0.12 } },
};

export const PostalCodeStatusIcon = ({ status }) => (
    <span className="tw:flex tw:h-5 tw:w-5 tw:items-center tw:justify-center">
        <AnimatePresence mode="wait" initial={false}>
            {status === POSTAL_CODE_STATUS.CHECKING && (
                <motion.span key="checking" {...iconSwap}>
                    <Spinner />
                </motion.span>
            )}
            {status === POSTAL_CODE_STATUS.FOUND && (
                <motion.span key="found" {...iconSwap}>
                    <DrawnCheck />
                </motion.span>
            )}
        </AnimatePresence>
    </span>
);

const messageSwap = {
    initial: { opacity: 0, height: 0, y: -4 },
    animate: { opacity: 1, height: "auto", y: 0, transition: { duration: 0.25, ease: [0.22, 1, 0.36, 1] } },
    exit: { opacity: 0, height: 0, transition: { duration: 0.15 } },
};

// `lng` (optional) pins the texts to one language — see CountryCodePicker.
export const PostalCodeStatusMessage = ({ status, onRetry, lng }) => {
    const { t: tInSiteLanguage, i18n } = useTranslation();
    const t = lng ? i18n.getFixedT(lng) : tInSiteLanguage;

    return (
        // aria-live: screen readers announce "Checking postal code…" and failures.
        <div aria-live="polite">
            <AnimatePresence mode="wait" initial={false}>
                {status === POSTAL_CODE_STATUS.CHECKING && (
                    <motion.p
                        key="checking"
                        {...messageSwap}
                        className="tw:!m-0 tw:overflow-hidden tw:pt-1.5 tw:text-[12px] tw:text-[#A3780F]"
                    >
                        {t("PostalCodeStatus.Checking")}
                    </motion.p>
                )}
                {status === POSTAL_CODE_STATUS.FAILED && (
                    <motion.p
                        key="failed"
                        {...messageSwap}
                        className="tw:!m-0 tw:overflow-hidden tw:pt-1.5 tw:text-[12px] tw:text-[#8D8D8D]"
                    >
                        {t("PostalCodeStatus.Failed")}{" "}
                        <button
                            type="button"
                            onClick={onRetry}
                            className="tw:font-semibold tw:text-[#A3780F] tw:underline tw:underline-offset-2"
                        >
                            {t("PostalCodeStatus.Retry")}
                        </button>
                    </motion.p>
                )}
            </AnimatePresence>
        </div>
    );
};
